import { Alert, Button, cn, Input, Link, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import CopyButton from 'components/Hero/CopyButton'
import { ExternalLink, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { $fetch, useFetch } from 'utils/fetch'

export default function SSOSettings() {
  const [showAddModal, setShowAddModal] = useState(false)

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm()

  const { data: providers = [], refetch, isLoading } = useFetch('/sso/providers')

  // Add provider mutation
  const addProviderMutation = useMutation({
    mutationFn: async (formData) => {
      const { data } = await $fetch(`/sso/providers`, {
        method: 'POST',
        body: {
          issuer: formData.issuer,
          domain: formData.domain,
          clientId: formData.clientId,
          clientSecret: formData.clientSecret,
          authorizationEndpoint: formData.authorizationEndpoint || undefined,
          tokenEndpoint: formData.tokenEndpoint || undefined,
          jwksEndpoint: formData.jwksEndpoint || undefined,
        },
      })
      return data
    },
    onSuccess: (data) => {
      toast.success('SSO provider configured successfully!')
      setShowAddModal(false)
      reset()
      refetch()

      // Show redirect URI info
      toast.info(`Redirect URI for your provider: ${data.redirectUri}`, {
        autoClose: 10000,
      })
    },
    onError: (error) => {
      console.error('Failed to add SSO provider:', error)
      const errorMessage = error.data?.errors?.[0]?.message || 'Failed to configure SSO provider'
      toast.error(errorMessage)
    },
  })

  // Delete provider mutation
  const deleteProviderMutation = useMutation({
    mutationFn: async (providerId) => {
      await $fetch(`/sso/providers/${providerId}`, {
        method: 'DELETE',
      })
      return providerId
    },
    onSuccess: () => {
      toast.success('SSO provider deleted successfully!')
      refetch()
    },
    onError: (error) => {
      console.error('Failed to delete SSO provider:', error)
      const errorMessage = error.data?.errors?.[0]?.message || 'Failed to delete SSO provider'
      toast.error(errorMessage)
    },
  })

  // Test provider mutation
  const testProviderMutation = useMutation({
    mutationFn: async (providerId) => {
      const { data } = await $fetch(`/sso/providers/${providerId}/test`, {
        method: 'POST',
      })
      return data
    },
    onSuccess: async (data) => {
      try {
        // Make POST request to initiate SSO flow
        const ssoResponse = await $fetch(data.ssoEndpoint, {
          method: 'POST',
          body: {
            providerId: data.providerId,
            callbackURL: data.callbackURL,
          },
        })

        // If successful, redirect to the SSO provider (Keycloak)
        if (ssoResponse?.redirect && ssoResponse?.url) {
          window.open(ssoResponse.url, '_blank', 'width=600,height=700')
          toast.info('Test window opened. Complete the SSO flow to test the connection.')
        }
        else {
          toast.error('Failed to initiate SSO flow - no redirect URL received')
        }
      }
      catch (error) {
        console.error('Failed to initiate SSO flow:', error)
        const errorMessage = error.data?.errors?.[0]?.message || 'Failed to initiate SSO flow'
        toast.error(errorMessage)
      }
    },
    onError: (error) => {
      console.error('Failed to test SSO provider:', error)
      const errorMessage = error.data?.errors?.[0]?.message || 'Failed to test SSO provider'
      toast.error(errorMessage)
    },
  })

  const handleAddProvider = (formData) => {
    addProviderMutation.mutate(formData)
  }

  const handleDeleteProvider = (providerId) => {
    deleteProviderMutation.mutate(providerId)
  }

  const handleTestProvider = (providerId) => {
    testProviderMutation.mutate(providerId)
  }

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">                
        <p className="text-sm text-gray-600">
          Configure OIDC providers to allow team members to sign in with their corporate credentials.
        </p>

        <Button
          color="primary"
          radius="none"
          startContent={<Plus size={16} />}
          onPress={() => setShowAddModal(true)}
          isDisabled={providers.length >= 5} // Limit to 5 providers
        >
          Add Provider
        </Button>
      </div>

      {/* Providers Table */}
      {providers.length > 0
        ? (
            <Table aria-label="SSO Providers" radius="none">
              <TableHeader>
                <TableColumn>DOMAIN</TableColumn>
                <TableColumn>ISSUER</TableColumn>
                <TableColumn>PROVIDER ID</TableColumn>
                <TableColumn>ACTIONS</TableColumn>
              </TableHeader>
              <TableBody>
                {providers.map(provider => (
                  <TableRow key={provider.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{provider.domain}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        href={provider.issuer}
                        target="_blank"
                        className={`
                          text-sm text-blue-600
                          hover:text-blue-800
                        `}
                        showAnchorIcon
                      >
                        {provider.issuer}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <CopyButton color="default" variant="flat" displayText={provider.providerId} value={provider.providerId} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="flat"
                          color="primary"
                          startContent={<ExternalLink size={14} />}
                          onPress={() => handleTestProvider(provider.providerId)}
                          isLoading={testProviderMutation.isPending && testProviderMutation.variables === provider.providerId}
                        >
                          Test
                        </Button>
                        <Button
                          size="sm"
                          variant="flat"
                          color="danger"
                          startContent={<Trash2 size={14} />}
                          onPress={() => handleDeleteProvider(provider.providerId)}
                          isLoading={deleteProviderMutation.isPending && deleteProviderMutation.variables === provider.providerId}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        : (
            <div className={`
              rounded-sm border border-gray-200 bg-gray-50 py-12 text-center
            `}
            >
              <h3 className="mb-2 text-lg font-medium text-gray-900">
                No SSO providers configured
              </h3>
              <p className="mb-4 text-gray-600">
                Set up OIDC providers to enable single sign-on for your organization.
              </p>
            </div>
          )}

      <Alert
        classNames={{
          base: cn(
            [
              `
                bg-default-50 shadow-sm
                dark:bg-background
              `,
              `
                border-1 border-default-200
                dark:border-default-100
              `,
              `
                relative
                before:absolute before:z-10 before:content-[\'\']
              `,
              'before:-top-px before:-bottom-px before:left-0 before:w-1',
              'rounded-l-none border-l-0',
              'before:bg-primary',
            ],
          ),
        }}
        color="primary"
        title="Important Notes"
      >
        <ul className="flex flex-col gap-1 text-sm text-blue-800">
          <li>• Users signing in via SSO will still need to verify their phone number during first-time setup</li>
          <li>• SSO users will be automatically added to this organization with the "operator" role, unless they have an admin role defined in the SSO provider</li>
          <li>• Each email domain can only be configured for one organization</li>
          <li>
            • The redirect URI for your OIDC provider should be:
            <code className="rounded bg-blue-100 px-1">https://api.bland.ai/authorization/sso/callback/[provider-id]</code>
            {' '}
            using the provider ID from the table above. Whitelist this URI
            <b>after</b>
            {' '}
            connecting the provider to Bland.
          </li>
        </ul>
      </Alert>

      {/* Add Provider Modal */}
      <Modal
        isOpen={showAddModal}
        onOpenChange={setShowAddModal}
        size="2xl"
        radius="none"
        scrollBehavior="inside"
      >
        <ModalContent>
          {onClose => (
            <form onSubmit={handleSubmit(handleAddProvider)}>
              <ModalHeader>Add OIDC Provider</ModalHeader>
              <ModalBody className="flex flex-col gap-4">
                <Input
                  label="Email Domain"
                  placeholder="example.com"
                  radius="none"
                  description="Users with this email domain will use this SSO provider"
                  isRequired
                  errorMessage={errors.domain?.message}
                  {...register('domain', {
                    required: 'Email domain is required',
                    pattern: {
                      value: /^[a-z0-9.-]+\.[a-z]{2,}$/i,
                      message: 'Please enter a valid domain (e.g., example.com)',
                    },
                  })}
                />

                <Input
                  label="Issuer URL"
                  placeholder="https://auth.example.com"
                  radius="none"
                  description="The OIDC issuer identifier URL"
                  isRequired
                  errorMessage={errors.issuer?.message}
                  {...register('issuer', {
                    required: 'Issuer URL is required',
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: 'Please enter a valid URL',
                    },
                  })}
                />

                <div className={`
                  grid grid-cols-1 gap-4
                  md:grid-cols-2
                `}
                >
                  <Input
                    label="Client ID"
                    placeholder="your-client-id"
                    isRequired
                    radius="none"
                    errorMessage={errors.clientId?.message}
                    {...register('clientId', { required: 'Client ID is required' })}
                  />

                  <Input
                    label="Client Secret"
                    placeholder="your-client-secret"
                    type="password"
                    isRequired
                    radius="none"
                    errorMessage={errors.clientSecret?.message}
                    {...register('clientSecret', { required: 'Client Secret is required' })}
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <h4 className="font-medium text-gray-900">Optional Endpoints</h4>
                  <p className="text-sm text-gray-600">
                    Leave blank to use auto-discovery from the issuer URL
                  </p>

                  <Input
                    label="Authorization Endpoint"
                    placeholder="https://auth.example.com/auth"
                    radius="none"
                    errorMessage={errors.authorizationEndpoint?.message}
                    {...register('authorizationEndpoint', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL',
                      },
                    })}
                  />

                  <Input
                    label="Token Endpoint"
                    placeholder="https://auth.example.com/token"
                    radius="none"
                    errorMessage={errors.tokenEndpoint?.message}
                    {...register('tokenEndpoint', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL',
                      },
                    })}
                  />

                  <Input
                    label="JWKS Endpoint"
                    placeholder="https://auth.example.com/jwks"
                    radius="none"
                    errorMessage={errors.jwksEndpoint?.message}
                    {...register('jwksEndpoint', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL',
                      },
                    })}
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" radius="none" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  radius="none"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Add Provider
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </div>
  )
}
